import React, { useState, useEffect } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useTour } from '../contexts/TourContext';
import { TOURS } from '../help/tours';

const SIDEBAR_STEPS = [
  { target: 'aside', content: 'Navigate TenderWright from the sidebar. All your tools are organized here.', placement: 'right', disableBeacon: true },
  { target: '[href="/proposals"]', content: 'Create and manage your engineering proposals here.', placement: 'right' },
  { target: '[href="/projects"]', content: 'Your project portfolio — past work that strengthens future proposals.', placement: 'right' },
  { target: '[href="/people"]', content: 'Your team members with qualifications like CPEng and CMEngNZ.', placement: 'right' },
  { target: '[href="/fee-estimator"]', content: 'Calculate fee estimates with NZ/AU rate cards and multipliers.', placement: 'right' },
  { target: '[href="/analytics"]', content: 'Track win rates, pipeline value, and team utilisation.', placement: 'right' },
];

export default function ProductTour() {
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState(SIDEBAR_STEPS);
  const { activeTourId, endTour } = useTour();

  // First-visit sidebar tour. Skip on mobile (sidebar is display:none and the
  // tour overlay points at nothing) and when ?skipTour is in the URL (so
  // Playwright/E2E tests can opt out cleanly without timing-dependent hacks).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth < 768) return;
    if (new URLSearchParams(window.location.search).has('skipTour')) return;
    const toured = localStorage.getItem('bidforge-toured');
    if (!toured) {
      const timer = setTimeout(() => {
        setSteps(SIDEBAR_STEPS);
        setRun(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Programmatic tours triggered via TourContext
  useEffect(() => {
    if (activeTourId) {
      const tour = TOURS[activeTourId];
      if (tour) {
        const tourKey = `bidforge-tour-${activeTourId}`;
        setSteps(tour.steps.map(s => ({ ...s, placement: s.placement || 'bottom' })));
        setTimeout(() => setRun(true), 300);
      }
    }
  }, [activeTourId]);

  const handleCallback = (data) => {
    // Mark the tour "seen" not just when the user explicitly finishes/skips
    // but also when they close the tooltip (action='close') or the Joyride
    // run ends for any reason ('tour:end'). Without this, closing the tab
    // mid-tour or hitting the X button leaves localStorage unset and the
    // tour re-fires on the next session — the exact reason every fresh
    // browser tab opens with the overlay blocking interactions.
    const persist =
      [STATUS.FINISHED, STATUS.SKIPPED, STATUS.PAUSED].includes(data.status)
      || data.action === 'close'
      || data.type === 'tour:end';
    if (persist) {
      setRun(false);
      if (activeTourId) {
        localStorage.setItem(`bidforge-tour-${activeTourId}`, 'true');
        endTour();
      } else {
        localStorage.setItem('bidforge-toured', 'true');
      }
    }
  };

  // Honour Escape as an explicit dismiss — defence-in-depth in case the
  // Joyride callback misses an event (it occasionally does on portal
  // re-mounts during rapid navigation).
  useEffect(() => {
    if (!run) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setRun(false);
        if (activeTourId) {
          localStorage.setItem(`bidforge-tour-${activeTourId}`, 'true');
          endTour();
        } else {
          localStorage.setItem('bidforge-toured', 'true');
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [run, activeTourId, endTour]);

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableCloseOnEsc={false}
      callback={handleCallback}
      styles={{
        options: { primaryColor: '#2196F3', zIndex: 10000 },
        tooltip: { borderRadius: 8, fontSize: 14 },
        buttonNext: { borderRadius: 6 },
      }}
    />
  );
}
